import React, { useState, useEffect } from "react";
import { Plus, Search, Edit, Trash2, Users, Upload } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import api from "../utils/api";
import { useSort } from "../utils/useSort";
import { formatMXN, formatDate } from "../utils/format";
import ImportModal from "../components/ImportModal";

function CustomerForm({ customer, onClose, onSave }) {
  const [form, setForm] = useState({
    name: "", rfc: "", email: "", phone: "", address: "",
    contact_name: "", notes: "", credit_days: 0
  });
  const [priceLists, setPriceLists] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/price-lists").then((r) => setPriceLists(r.data));
    if (customer) setForm({ ...customer });
  }, [customer]);

  const handleSave = async () => {
    if (!form.name) { toast.error("El nombre es requerido"); return; }
    setSaving(true);
    try {
      if (customer) {
        await api.put(`/customers/${customer.id}`, form);
        toast.success("Cliente actualizado");
      } else {
        await api.post("/customers", form);
        toast.success("Cliente creado");
      }
      onSave();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  const fields = [
    { key: "name", label: "Nombre *", type: "text", col: 2 },
    { key: "rfc", label: "RFC", type: "text", col: 1 },
    { key: "phone", label: "Teléfono", type: "text", col: 1 },
    { key: "email", label: "Correo electrónico", type: "email", col: 2 },
    { key: "contact_name", label: "Nombre de contacto", type: "text", col: 1 },
    { key: "credit_days", label: "Días de crédito", type: "number", col: 1 },
    { key: "address", label: "Dirección", type: "text", col: 2 },
    { key: "notes", label: "Notas", type: "textarea", col: 2 },
  ];

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{customer ? "Editar cliente" : "Nuevo cliente"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-2">
          {fields.map((f) => (
            <div key={f.key} className={f.col === 2 ? "col-span-2" : ""}>
              <label className="block text-xs text-slate-500 mb-1">{f.label}</label>
              {f.type === "textarea" ? (
                <textarea value={form[f.key] || ""} onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
                  rows={3} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
              ) : (
                <input type={f.type} value={form[f.key] || ""} onChange={(e) => setForm((p) => ({ ...p, [f.key]: f.type === "number" ? Number(e.target.value) : e.target.value }))}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
              )}
            </div>
          ))}
          <div className="col-span-2">
            <label className="block text-xs text-slate-500 mb-1">Lista de precios asignada</label>
            <select value={form.price_list_id || ""} onChange={(e) => setForm((p) => ({ ...p, price_list_id: e.target.value || null }))}
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500">
              <option value="">Lista de precios general</option>
              {priceLists.map((pl) => <option key={pl.id} value={pl.id}>{pl.name}</option>)}
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">Cancelar</button>
          <button onClick={handleSave} disabled={saving}
            className="px-6 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-60">
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Clientes() {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showImport, setShowImport] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/customers"); setCustomers(r.data); }
    catch { toast.error("Error al cargar clientes"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este cliente?")) return;
    await api.delete(`/customers/${id}`);
    toast.success("Cliente eliminado");
    load();
  };

  const filtered = customers.filter((c) =>
    !search || c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.email?.toLowerCase().includes(search.toLowerCase()) ||
    c.phone?.includes(search)
  );
  const { sorted, SortIcon, handleSort } = useSort(filtered, "name");

  return (
    <div className="space-y-4" data-testid="clientes-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Clientes</h1>
          <p className="text-slate-500 text-sm mt-0.5">{customers.length} clientes registrados</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowImport(true)}
            className="flex items-center gap-1.5 border border-slate-200 hover:bg-slate-50 text-slate-600 px-3 py-2 rounded-lg text-sm font-medium transition-colors">
            <Upload size={15} /> <span className="hidden sm:inline">Importar Excel</span><span className="sm:hidden">Excel</span>
          </button>
          <button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="nuevo-cliente-btn"
            className="flex items-center gap-1.5 bg-brand-600 hover:bg-brand-700 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors">
            <Plus size={15} /> <span className="hidden sm:inline">Nuevo cliente</span><span className="sm:hidden">Nuevo</span>
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="relative max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input placeholder="Buscar cliente..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500" />
        </div>
      </div>

      {/* Desktop table */}
      <div className="hidden sm:block bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="customers-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort("name")}>Nombre <SortIcon col="name" /></th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort("rfc")}>RFC <SortIcon col="rfc" /></th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Teléfono</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort("email")}>Correo <SortIcon col="email" /></th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort("balance")}>Saldo <SortIcon col="balance" /></th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-12 text-slate-400">Cargando...</td></tr>
            ) : sorted.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-12 text-slate-400">Sin clientes</td></tr>
            ) : sorted.map((c) => (
              <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                <td className="px-4 py-3 font-medium text-slate-900">{c.name}</td>
                <td className="px-4 py-3 text-slate-500 text-xs">{c.rfc || "-"}</td>
                <td className="px-4 py-3 text-slate-600">{c.phone || "-"}</td>
                <td className="px-4 py-3 text-slate-600">{c.email || "-"}</td>
                <td className="px-4 py-3">
                  <span className={c.balance > 0 ? "text-red-600 font-medium" : "text-slate-500"}>
                    {formatMXN(c.balance || 0)}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    <button onClick={() => { setEditing(c); setShowForm(true); }} className="p-1.5 hover:bg-slate-100 rounded-md transition-colors">
                      <Edit size={14} className="text-slate-500" />
                    </button>
                    <button onClick={() => handleDelete(c.id)} className="p-1.5 hover:bg-red-50 rounded-md transition-colors">
                      <Trash2 size={14} className="text-red-400" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="sm:hidden space-y-2">
        {loading ? (
          <div className="text-center py-12 text-slate-400">Cargando...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-slate-400">Sin clientes</div>
        ) : filtered.map((c) => (
          <div key={c.id} className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-900 truncate">{c.name}</div>
                {c.rfc && <div className="text-xs text-slate-400 mt-0.5">RFC: {c.rfc}</div>}
                <div className="flex flex-wrap gap-x-3 mt-2 text-xs text-slate-500">
                  {c.phone && <span>📞 {c.phone}</span>}
                  {c.email && <span className="truncate">✉️ {c.email}</span>}
                </div>
                {c.balance > 0 && (
                  <div className="mt-2 text-xs font-medium text-red-600">Saldo: {formatMXN(c.balance)}</div>
                )}
              </div>
              <div className="flex gap-1 ml-2">
                <button onClick={() => { setEditing(c); setShowForm(true); }} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
                  <Edit size={15} className="text-slate-500" />
                </button>
                <button onClick={() => handleDelete(c.id)} className="p-2 hover:bg-red-50 rounded-lg transition-colors">
                  <Trash2 size={15} className="text-red-400" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <CustomerForm customer={editing} onClose={() => setShowForm(false)} onSave={() => { setShowForm(false); load(); }} />
      )}
      {showImport && (
        <ImportModal type="customers" onClose={() => setShowImport(false)} onSuccess={() => { setShowImport(false); load(); }} />
      )}
    </div>
  );
}
